import React, { useEffect, useState, useRef, useMemo } from "react";
import {
    Checkbox,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Popper,
    Select,
    Stack,
    Tooltip,
    Typography,
    Box,
    Button,
    Chip,
    Collapse,
    CircularProgress,
    Link,
    PDCAlpha as alpha,
    ClickAwayListener,
    ListItemButton,
} from "@mzc-pdc/ui";
import { useTranslation } from "react-i18next";
import { TabPanel, Tabs } from "@components_v2/tabs/tabs";
import {
    IconAllView,
    IconArrowDown,
    IconArrowUp,
    IconAssetsAdd,
    IconCancel,
    IconCancelCircle,
    IconCategory,
    IconCircle,
    IconClose,
    IconCompleteCircle,
    IconDownload,
    IconErrorCircle,
    IconFile,
    IconFileCopyto,
    IconHierarchyFolded,
    IconJobs,
    IconOpenInNew,
    IconPath,
    IconProjects,
    IconRetry,
    IconTime,
    IconZip,
    ImageEmptyItem,
    ImageFolderSmallOn,
} from "@mzc-cloudplex/icons";
import theme from "@styles/theme";
import State from "@components/state/State";
import AssetTypeFlag from "@components_v2/icon-type/asset/asset-type-flag";
import compareVersions, {
    VERSION_CM_4750_UPLOAD_FOLDERS,
    VERSION_CM_7307_DOWNLOAD,
    VERSION_CM_7349_CREATE_ASSETS_BULK,
    VERSION_MY_JOBS_PANEL,
} from "@cores/version";
import { JOB_PANEL_TABS } from "@constants";
import { cancelTranscode } from "@modules/upload";
import { changeActiveTab, inActive } from "@modules/rightSideBar";
import { changeFilesOffset } from "@modules/uploadQueue";
import { useDispatch, useSelector } from "react-redux";
import menuService from "@services/menuService";
import { useToggle } from "@mzc-pdc/hooks";
import StatusBadge from "@components/statusbadge";
import * as jobStatus from "@cores/jobStatus";
import MyJobProgressCard from "@components/myjob";
import VersionedComponent from "@components/VersionedComponent";
import FileJobPanel from "@routes/rightsidebar/ui/FileJobList";
import UploadFilesPanel from "@routes/rightsidebar/ui/UploadFilesPanel";
import DownloadJobPanel from "@routes/rightsidebar/ui/DownloadJobList";
import CreateAssetsPanel from "@routes/rightsidebar/ui/CreateAssetsJobList";
import TranscodeJobList from "@routes/rightsidebar/ui/transcode-job-list";

export const MyJobListV2 = ({ jobs, location }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { spaceId, projectId } = menuService.parse(location.pathname);

    const [localFilesOffset, setLocalFilesOffset] = useState(0);
    const { active: tabListPopperActive, toggle: tabListPopperToggle } = useToggle();

    const [selectedTab, setSelectedTab] = useState("");
    const [anchorEl, setAnchorEl] = useState(null);
    const anchorElRef = useRef(null);

    const stage = useSelector(({ stage }) => stage);
    const project = useSelector(({ project }) => project);
    const uploadQueue = useSelector(({ uploadQueue }) => uploadQueue);
    const myJobs = useSelector(({ myJobs }) => myJobs);
    const downloadJobs = useSelector(({ downloadJobs }) => downloadJobs);
    const bulkJobs = useSelector(({ bulkJobs }) => bulkJobs);
    const activeTab = useSelector(({ rightSideBar }) => rightSideBar.activeTab);

    const open = Boolean(anchorEl);
    const id = open ? "simple-popover" : undefined;

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const [category, setCategory] = useState([]);
    const [status, setStatus] = useState([]);

    const handleChangeCategory = (event) => {
        const {
            target: { value },
        } = event;
        setCategory(typeof value === "string" ? value.split(",") : value);
    };

    const handleChangeStatus = (event) => {
        const {
            target: { value },
        } = event;
        setStatus(typeof value === "string" ? value.split(",") : value);
    };

    const onHandleChangeTab = (tab) => {
        dispatch(changeActiveTab({ activeTab: tab }));
    };

    const onChangeLimit = (offset) => {
        setLocalFilesOffset(offset);
        dispatch(changeFilesOffset(offset));
    };

    const tabList = useMemo(() => {
        const newTabList = [];

        if (compareVersions(stage.version, VERSION_CM_7349_CREATE_ASSETS_BULK) >= 0) {
            newTabList.push({
                label: t(`common::label::${JOB_PANEL_TABS.ASSET_JOBS}`, `${JOB_PANEL_TABS.ASSET_JOBS}`),
                value: JOB_PANEL_TABS.ASSET_JOBS,
            });
        }
        if (compareVersions(stage.version, VERSION_CM_7307_DOWNLOAD) >= 0) {
            newTabList.push({
                label: t(`common::label::${JOB_PANEL_TABS.DOWNLOAD}`, `${JOB_PANEL_TABS.DOWNLOAD}`),
                value: JOB_PANEL_TABS.DOWNLOAD,
            });
        }
        newTabList.push({
            label: t(`common::label::${JOB_PANEL_TABS.TRANSCODE}`, `${JOB_PANEL_TABS.TRANSCODE}`),
            value: JOB_PANEL_TABS.TRANSCODE,
        });
        if (compareVersions(stage.version, VERSION_CM_4750_UPLOAD_FOLDERS) >= 0) {
            newTabList.push({
                label: t(`common::label::${JOB_PANEL_TABS.SOURCE_UPLOAD}`, `${JOB_PANEL_TABS.SOURCE_UPLOAD}`),
                value: JOB_PANEL_TABS.SOURCE_UPLOAD,
            });
        }
        if (compareVersions(stage.version, VERSION_MY_JOBS_PANEL) >= 0) {
            newTabList.push({
                label: t(`common::label::${JOB_PANEL_TABS.SOURCE_JOBS}`, `${JOB_PANEL_TABS.SOURCE_JOBS}`),
                value: JOB_PANEL_TABS.SOURCE_JOBS,
            });
        }

        return newTabList;
    }, [stage.version]);

    useEffect(() => {
        const defaultOffset = uploadQueue.local_files_offset !== 50 ? 50 : uploadQueue.local_files_offset;
        setLocalFilesOffset(defaultOffset);
    }, [uploadQueue.local_files_offset]);

    useEffect(() => {
        let initialTab = null;
        if (compareVersions(stage.version, VERSION_CM_7349_CREATE_ASSETS_BULK) >= 0) {
            initialTab = JOB_PANEL_TABS.ASSET_JOBS;
        } else if (compareVersions(stage.version, VERSION_CM_7307_DOWNLOAD) >= 0) {
            initialTab = JOB_PANEL_TABS.DOWNLOAD;
        } else {
            initialTab = JOB_PANEL_TABS.TRANSCODE;
        }
        dispatch(changeActiveTab({ activeTab: initialTab }));
    }, [stage.version]);

    return (
        <Stack height={1}>
            <Grid
                container
                minHeight={theme.mixins.toolbar.minHeight}
                alignItems={"center"}
                justifyContent={"space-between"}
                px={1.5}
                gap={1.5}
            >
                <Grid item>
                    <Typography variant="subtitle1_700">{t("My Jobs", "My Jobs")}</Typography>
                </Grid>
                <Grid item>
                    <Tooltip title={t(`common::label::Close my jobs`, `Close my jobs`)}>
                        <IconButton circled size={"medium"} onClick={() => dispatch(inActive())}>
                            <IconCancel size={20} />
                        </IconButton>
                    </Tooltip>
                </Grid>
            </Grid>
            <Divider />
            <Grid
                container
                flexDirection={"row"}
                alignItems={"center"}
                flexWrap={"nowrap"}
                sx={{ borderBottom: `1px solid ${theme.palette.greySecondary.A100}` }}
            >
                <Grid item xs zeroMinWidth>
                    <Tabs
                        value={activeTab}
                        items={tabList}
                        onChangeTab={onHandleChangeTab}
                        indicatorPosition={"bottom"}
                        isBordered={false}
                        textColor={"secondary"}
                        variant={"scrollable"}
                        scrollButtons={"auto"}
                        scrollButtonPosition={"right"}
                        divider={
                            <Divider
                                orientation={"vertical"}
                                color={theme.palette.greySecondary.A100}
                                sx={{ height: "20px", mx: 1 }}
                            />
                        }
                        sx={{
                            ".MuiTabScrollButton-root.MuiTabScrollButton-horizontal": {
                                borderTop: 0,
                                borderBottom: 0,
                            },
                        }}
                    />
                </Grid>
                <Grid
                    item
                    ref={anchorElRef}
                    width={40}
                    height={1}
                    textAlign={"center"}
                    bgcolor={open ? "greySecondary.main" : "inherit"}
                    display={"flex"}
                    alignItems={"center"}
                    justifyContent={"center"}
                >
                    <Tooltip title={t(`common::label::Open tab menu`, `Open tab menu`)}>
                        <IconButton circled size={"small"} onClick={tabListPopperToggle}>
                            <IconAllView size={16} />
                        </IconButton>
                    </Tooltip>
                    <Popper
                        open={tabListPopperActive}
                        anchorEl={anchorElRef.current}
                        onClose={handleClose}
                        placement={"bottom-end"}
                        sx={{
                            marginTop: `-40px !important `,
                            zIndex: theme.zIndex.tooltip,
                            width: 286,
                            bgcolor: "background.paper",
                            boxShadow: `0px 2px 4px 0px rgba(0, 0, 0, 0.12)`,
                        }}
                    >
                        <ClickAwayListener
                            mouseEvent={tabListPopperActive ? "onClick" : false}
                            onClickAway={() => {
                                tabListPopperToggle();
                            }}
                        >
                            <Stack>
                                <Grid container pl={2} alignItems={"center"} justifyContent={"space-between"}>
                                    <Grid item>
                                        <Grid container height={40} gap={0.75} alignItems={"center"}>
                                            <IconJobs size={16} />
                                            <Typography variant={"body3_500"}>{t("Jobs", "Jobs")}</Typography>
                                        </Grid>
                                    </Grid>
                                    <Grid
                                        item
                                        width={40}
                                        height={40}
                                        textAlign={"center"}
                                        bgcolor={tabListPopperActive ? "greySecondary.main" : "inherit"}
                                        display={"flex"}
                                        alignItems={"center"}
                                        justifyContent={"center"}
                                    >
                                        <Tooltip title={t(`common::label::Close tab menu`, `Close tab menu`)}>
                                            <IconButton
                                                circled
                                                size={"small"}
                                                variant={"contained"}
                                                color={"greySecondary"}
                                                onClick={tabListPopperToggle}
                                            >
                                                <IconClose size={16} />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                </Grid>
                                <Divider />
                                <List sx={{ maxHeight: 260, overflowY: "auto" }}>
                                    {tabList.map((item, index) => (
                                        <ListItem
                                            sx={{
                                                padding: `0`,
                                                height: "48px",
                                                "&:hover": {
                                                    backgroundColor: alpha(theme.palette.greySecondary[50], 0.24),
                                                    textDecoration: "underline",
                                                },
                                            }}
                                            data-id={item.id}
                                            onClick={() => {
                                                onHandleChangeTab(item.value);
                                            }}
                                        >
                                            <ListItemButton
                                                selected={activeTab === item.value}
                                                sx={{
                                                    height: "100%",
                                                    "&.Mui-selected": {
                                                        backgroundColor: alpha(theme.palette.greySecondary.A100, 0.4),
                                                    },
                                                }}
                                            >
                                                <ListItemText
                                                    primary={
                                                        <Grid item>
                                                            <Typography
                                                                variant={`body1_${
                                                                    activeTab === item.value ? "500" : "400"
                                                                }`}
                                                            >
                                                                {item.label}
                                                            </Typography>
                                                        </Grid>
                                                    }
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    ))}
                                </List>
                            </Stack>
                        </ClickAwayListener>
                    </Popper>
                </Grid>
            </Grid>
            <TabPanel
                value={activeTab}
                index={JOB_PANEL_TABS.TRANSCODE}
                sx={{ height: "100%", display: "flex", flexDirection: "column" }}
            >
                <TranscodeJobList jobs={jobs} />
            </TabPanel>

            <VersionedComponent version={VERSION_MY_JOBS_PANEL}>
                <TabPanel
                    value={activeTab}
                    index={JOB_PANEL_TABS.SOURCE_JOBS}
                    sx={{ height: "100%", display: "flex", flexDirection: "column" }}
                >
                    <FileJobPanel />
                </TabPanel>
            </VersionedComponent>
            <VersionedComponent version={VERSION_CM_4750_UPLOAD_FOLDERS}>
                <TabPanel
                    value={activeTab}
                    index={JOB_PANEL_TABS.SOURCE_UPLOAD}
                    sx={{ height: "100%", display: "flex", flexDirection: "column" }}
                >
                    <UploadFilesPanel local_files_offset={localFilesOffset} onChangeLimit={onChangeLimit} />
                </TabPanel>
            </VersionedComponent>
            <VersionedComponent version={VERSION_CM_7307_DOWNLOAD}>
                <TabPanel
                    value={activeTab}
                    index={JOB_PANEL_TABS.DOWNLOAD}
                    sx={{ height: "100%", display: "flex", flexDirection: "column" }}
                >
                    <DownloadJobPanel />
                </TabPanel>
            </VersionedComponent>
            <VersionedComponent version={VERSION_CM_7349_CREATE_ASSETS_BULK}>
                <TabPanel
                    value={activeTab}
                    index={JOB_PANEL_TABS.ASSET_JOBS}
                    sx={{ height: "100%", display: "flex", flexDirection: "column" }}
                >
                    <CreateAssetsPanel />
                </TabPanel>
            </VersionedComponent>
            {/* 선택 영역 */}
            {/*<Grid container pt={3} px={3} pb={2} gap={1} justifyContent={"center"} alignItems={"center"}>*/}
            {/*    <Grid item xs>*/}
            {/*        <FormControl fullWidth>*/}
            {/*            <InputLabel id="select-label-category">유형</InputLabel>*/}
            {/*            <Select*/}
            {/*                labelId="select-label-category"*/}
            {/*                id="select-category"*/}
            {/*                value={category}*/}
            {/*                multiple*/}
            {/*                onChange={handleChangeCategory}*/}
            {/*                input={<OutlinedInput label="유형" />}*/}
            {/*                renderValue={(selected) => selected.join(", ")}*/}
            {/*                defaultValue={sampleCategories[0]}*/}
            {/*            >*/}
            {/*                {sampleCategories.map((name) => (*/}
            {/*                    <MenuItem key={name} value={name}>*/}
            {/*                        <Checkbox color={"greySecondary"} checked={category.indexOf(name) > -1} />*/}
            {/*                        <ListItemText primary={name} />*/}
            {/*                    </MenuItem>*/}
            {/*                ))}*/}
            {/*            </Select>*/}
            {/*        </FormControl>*/}
            {/*    </Grid>*/}
            {/*    <Grid item xs>*/}
            {/*        <FormControl fullWidth>*/}
            {/*            <InputLabel id="select-label-status">상태</InputLabel>*/}
            {/*            <Select*/}
            {/*                labelId="select-label-status"*/}
            {/*                id="select-status"*/}
            {/*                value={status}*/}
            {/*                onChange={handleChangeStatus}*/}
            {/*                multiple*/}
            {/*                input={<OutlinedInput label="상태" />}*/}
            {/*                renderValue={(selected) => selected.join(", ")}*/}
            {/*                defaultValue={sampleStatus[0]}*/}
            {/*            >*/}
            {/*                {sampleStatus.map((name) => (*/}
            {/*                    <MenuItem key={name} value={name}>*/}
            {/*                        <Checkbox color={"greySecondary"} checked={status.indexOf(name) > -1} />*/}
            {/*                        <ListItemText primary={name} />*/}
            {/*                    </MenuItem>*/}
            {/*                ))}*/}
            {/*            </Select>*/}
            {/*        </FormControl>*/}
            {/*    </Grid>*/}
            {/*</Grid>*/}
            {/*<Divider sx={{ mx: 3 }} />*/}
            {/*/!* //선택 영역 *!/*/}
            {/*/!* 목록 영역 *!/*/}
            {/*<Stack flex={1} height={1} px={3} py={2.5} gap={4}>*/}
            {/*    /!* empty *!/*/}
            {/*    <Stack py={5} gap={1.5} alignItems={"center"} justifyContent={"center"}>*/}
            {/*        <ImageEmptyItem width={80} height={72} />*/}
            {/*        <Typography variant={"body1"}>아직 작업이 없습니다.</Typography>*/}
            {/*    </Stack>*/}
            {/*    /!* //empty *!/*/}

            {/*    /!* item *!/*/}
            {/*    <Box>*/}
            {/*        <Grid container pb={1} gap={1} alignItems={"center"}>*/}
            {/*            <Grid item>*/}
            {/*                <Grid*/}
            {/*                    container*/}
            {/*                    alignItems={"center"}*/}
            {/*                    justifyContent={"center"}*/}
            {/*                    width={24}*/}
            {/*                    height={20}*/}
            {/*                    bgcolor={"greySecondary.main"}*/}
            {/*                    color={"greySecondary.contrastText"}*/}
            {/*                    borderRadius={0.7}*/}
            {/*                >*/}
            {/*                    <IconDownload size={16} />*/}
            {/*                    /!*<IconFileCopyto size={16}/>*!/*/}
            {/*                    /!*<IconAssetsAdd size={16}/>*!/*/}
            {/*                </Grid>*/}
            {/*            </Grid>*/}
            {/*            <Divider flexItem orientation={"vertical"} sx={{ my: 1 }} />*/}
            {/*            <Grid item xs>*/}
            {/*                <Grid container gap={1} alignItems={"center"}>*/}
            {/*                    <State variant={"contained"} status={"SUBMITTED"} />*/}
            {/*                    <Tooltip title={"오류 메시지"}>*/}
            {/*                        <IconButton circled size="small">*/}
            {/*                            <IconErrorCircle size={16} color="error" />*/}
            {/*                        </IconButton>*/}
            {/*                    </Tooltip>*/}
            {/*                </Grid>*/}
            {/*            </Grid>*/}
            {/*            <Grid item>*/}
            {/*                <Button*/}
            {/*                    variant={"text"}*/}
            {/*                    size={"small"}*/}
            {/*                    color={"greySecondary"}*/}
            {/*                    startIcon={<IconCancelCircle size={16} />}*/}
            {/*                >*/}
            {/*                    취소*/}
            {/*                </Button>*/}
            {/*                <Button*/}
            {/*                    variant={"text"}*/}
            {/*                    size={"small"}*/}
            {/*                    color={"greySecondary"}*/}
            {/*                    startIcon={<IconRetry size={16} />}*/}
            {/*                >*/}
            {/*                    재시도*/}
            {/*                </Button>*/}
            {/*            </Grid>*/}
            {/*        </Grid>*/}
            {/*        <Divider />*/}
            {/*        <Stack py={2.5} px={1.5} gap={1.5}>*/}
            {/*            <Grid container gap={1}>*/}
            {/*                <Grid item xs={3}>*/}
            {/*                    <Grid container gap={1} alignItems={"center"}>*/}
            {/*                        <IconCircle size={6} htmlColor={theme.palette.greySecondary[500]} />*/}
            {/*                        작업*/}
            {/*                    </Grid>*/}
            {/*                </Grid>*/}
            {/*                <Grid item>자산 대량 생성</Grid>*/}
            {/*            </Grid>*/}
            {/*            <Grid container gap={1}>*/}
            {/*                <Grid item xs={3}>*/}
            {/*                    <Grid container gap={1} alignItems={"center"}>*/}
            {/*                        <IconCircle size={6} htmlColor={theme.palette.greySecondary[500]} />*/}
            {/*                        작업 ID*/}
            {/*                    </Grid>*/}
            {/*                </Grid>*/}
            {/*                <Grid item>*/}
            {/*                    1564654*/}
            {/*                    <Link href="#" underline="hover">*/}
            {/*                        1564654*/}
            {/*                        <IconOpenInNew size={16} />*/}
            {/*                    </Link>*/}
            {/*                </Grid>*/}
            {/*            </Grid>*/}
            {/*            <Grid container gap={1}>*/}
            {/*                <Grid item xs={3}>*/}
            {/*                    <Grid container gap={1} alignItems={"center"}>*/}
            {/*                        <IconCircle size={6} htmlColor={theme.palette.greySecondary[500]} />*/}
            {/*                        시작 일시*/}
            {/*                    </Grid>*/}
            {/*                </Grid>*/}
            {/*                <Grid item>2024-03-19 11:45:35</Grid>*/}
            {/*            </Grid>*/}
            {/*        </Stack>*/}
            {/*        <Collapse in={!collapsed} collapsedSize={75}>*/}
            {/*            <Box*/}
            {/*                px={1}*/}
            {/*                py={1.5}*/}
            {/*                borderRadius={1}*/}
            {/*                bgcolor={alpha(theme.palette.greySecondary[50], 0.5)}*/}
            {/*                maxHeight={500}*/}
            {/*                overflow={"auto"}*/}
            {/*            >*/}
            {/*                <Grid container gap={1.5}>*/}
            {/*                    <Grid item>*/}
            {/*                        <Tooltip title={collapsed ? "펼침" : "접힘"}>*/}
            {/*                            <IconButton*/}
            {/*                                circled*/}
            {/*                                size="xxsmall"*/}
            {/*                                onClick={handleChangeCollapsed}*/}
            {/*                                sx={{ width: 20, height: 20 }}*/}
            {/*                            >*/}
            {/*                                {collapsed ? <IconArrowDown size={12} /> : <IconArrowUp size={12} />}*/}
            {/*                            </IconButton>*/}
            {/*                        </Tooltip>*/}
            {/*                    </Grid>*/}
            {/*                    <Grid item xs>*/}
            {/*                        <Stack gap={1.5}>*/}
            {/*                            <Grid container>*/}
            {/*                                <Grid item xs>*/}
            {/*                                    <Grid container mt={0.25} mb={1.5} alignItems={"center"}>*/}
            {/*                                        <Typography variant={"body3_700"} color="primary.main">*/}
            {/*                                            100*/}
            {/*                                        </Typography>*/}
            {/*                                        <Typography variant={"body3_400"}>개의 작업 상세보기</Typography>*/}
            {/*                                    </Grid>*/}
            {/*                                    <Grid container gap={0.75} alignItems={"center"}>*/}
            {/*                                        /!* 생성중 *!/*/}
            {/*                                        <CircularProgress size={12} thickness={5} color="primary" />*/}
            {/*                                        <Typography color="primary.main">자산 생성 중...</Typography>*/}
            {/*                                        /!* 취소 *!/*/}
            {/*                                        <Typography>자산 복제가 취소되었습니다.</Typography>*/}
            {/*                                        /!* 준비 완료 *!/*/}
            {/*                                        <Typography color="success.main">*/}
            {/*                                            자산 다운로드 준비가 완료되었습니다.*/}
            {/*                                        </Typography>*/}
            {/*                                        /!* 기간 만료 *!/*/}
            {/*                                        <Typography color="warning.main">*/}
            {/*                                            자산 다운로드 기간이 만료되었습니다.*/}
            {/*                                        </Typography>*/}
            {/*                                        /!* 오류 발생 *!/*/}
            {/*                                        <Typography color="error.main">*/}
            {/*                                            자산 다운로드를 준비하는 동안 오류가 발생했습니다.*/}
            {/*                                        </Typography>*/}
            {/*                                    </Grid>*/}
            {/*                                </Grid>*/}
            {/*                                /!* 버튼이 있을 경우만 노출 *!/*/}
            {/*                                <Grid item>*/}
            {/*                                    <Button*/}
            {/*                                        size="small"*/}
            {/*                                        color="greySecondary"*/}
            {/*                                        startIcon={<IconDownload size="16" />}*/}
            {/*                                    >*/}
            {/*                                        다운로드*/}
            {/*                                    </Button>*/}
            {/*                                    <Button size="small" color="error" startIcon={<IconTime size="16" />}>*/}
            {/*                                        만료됨*/}
            {/*                                    </Button>*/}
            {/*                                </Grid>*/}
            {/*                            </Grid>*/}
            {/*                        </Stack>*/}
            {/*                        /!* 대량 생성 *!/*/}
            {/*                        <Stack py={1.5} gap={2}>*/}
            {/*                            <Box>*/}
            {/*                                <Grid container alignItems={"center"} flexWrap={"nowrap"}>*/}
            {/*                                    <AssetTypeFlag width={20} height={16} type={"VIDEO"} />*/}
            {/*                                    <Grid item>*/}
            {/*                                        <Typography variant="body2_500">자산 이름1</Typography>*/}
            {/*                                    </Grid>*/}
            {/*                                </Grid>*/}
            {/*                                <Grid container gap={1} mt={0.75} px={3.5} flexWrap={"nowrap"}>*/}
            {/*                                    <IconPath size={16} htmlColor={theme.palette.greySecondary[500]} />*/}
            {/*                                    <Typography variant="body3_400" color={"text.secondary"}>*/}
            {/*                                        Drive/FolderName/FolderName/FolderName/FolderName/FolderName/FolderName/FolderName/FolderName/FolderName/FolderName*/}
            {/*                                    </Typography>*/}
            {/*                                </Grid>*/}
            {/*                            </Box>*/}
            {/*                            <Box>*/}
            {/*                                <Grid container alignItems={"center"} flexWrap={"nowrap"}>*/}
            {/*                                    <AssetTypeFlag width={20} height={16} type={"AUDIO"} />*/}
            {/*                                    <Grid item xs>*/}
            {/*                                        <Typography variant="body2_500">자산 이름1</Typography>*/}
            {/*                                        <Typography color="primary.main" variant="body3_400" sx={{ ml: 1 }}>*/}
            {/*                                            (*/}
            {/*                                            <Link href="#" underline="hover">*/}
            {/*                                                1564654*/}
            {/*                                                <IconOpenInNew size={12} />*/}
            {/*                                            </Link>*/}
            {/*                                            )*/}
            {/*                                        </Typography>*/}
            {/*                                    </Grid>*/}
            {/*                                    <Grid item>*/}
            {/*                                        <Tooltip title={"오류 메시지"}>*/}
            {/*                                            <IconButton circled size="small">*/}
            {/*                                                <IconErrorCircle size={16} color="error" />*/}
            {/*                                            </IconButton>*/}
            {/*                                        </Tooltip>*/}
            {/*                                    </Grid>*/}
            {/*                                </Grid>*/}
            {/*                                <Grid container gap={1} mt={0.75} px={3.5} flexWrap={"nowrap"}>*/}
            {/*                                    <IconPath size={16} htmlColor={theme.palette.greySecondary[500]} />*/}
            {/*                                    <Typography variant="body3_400" color={"text.secondary"}>*/}
            {/*                                        Drive/FolderName/FolderName/FolderName/FolderName/FolderName/FolderName/FolderName/FolderName/FolderName/FolderName*/}
            {/*                                    </Typography>*/}
            {/*                                </Grid>*/}
            {/*                            </Box>*/}
            {/*                        </Stack>*/}
            {/*                        /!* //대량 생성 *!/*/}

            {/*                        /!* 복제 *!/*/}
            {/*                        <Stack py={1.5} gap={2}>*/}
            {/*                            <Box>*/}
            {/*                                <Typography variant="body3_700" color="text.secondary">*/}
            {/*                                    기존 위치*/}
            {/*                                </Typography>*/}
            {/*                                <Stack mt={1} mb={2} px={1} gap={1}>*/}
            {/*                                    <Grid container gap={1} alignItems={"center"} flexWrap={"nowrap"}>*/}
            {/*                                        <IconProjects*/}
            {/*                                            size={16}*/}
            {/*                                            htmlColor={theme.palette.greySecondary[500]}*/}
            {/*                                        />*/}
            {/*                                        <Typography variant="body3_400">자산 이름1</Typography>*/}
            {/*                                    </Grid>*/}
            {/*                                    <Grid container gap={1} flexWrap={"nowrap"}>*/}
            {/*                                        <Grid item>*/}
            {/*                                            <IconCategory*/}
            {/*                                                size={16}*/}
            {/*                                                htmlColor={theme.palette.greySecondary[500]}*/}
            {/*                                            />*/}
            {/*                                        </Grid>*/}
            {/*                                        <Grid item>*/}
            {/*                                            <Grid container gap={0.5}>*/}
            {/*                                                <Chip label="카테고리1" size="small" variant="outlined" />*/}
            {/*                                                <Chip label="카테고리1" size="small" variant="outlined" />*/}
            {/*                                                <Chip label="카테고리1" size="small" variant="outlined" />*/}
            {/*                                                <Chip label="카테고리1" size="small" variant="outlined" />*/}
            {/*                                                <Chip label="카테고리1" size="small" variant="outlined" />*/}
            {/*                                            </Grid>*/}
            {/*                                        </Grid>*/}
            {/*                                    </Grid>*/}
            {/*                                </Stack>*/}
            {/*                                <Grid container alignItems={"center"} flexWrap={"nowrap"}>*/}
            {/*                                    /!* 1개 *!/*/}
            {/*                                    <AssetTypeFlag width={20} height={16} type={"VIDEO"} />*/}
            {/*                                    <Grid item>*/}
            {/*                                        <Typography variant="body2_500">자산 이름1 외</Typography>*/}
            {/*                                        <Typography color="primary.main" variant="body2_500">*/}
            {/*                                            99*/}
            {/*                                        </Typography>*/}
            {/*                                        <Typography variant="body2_500">개</Typography>*/}
            {/*                                        <Typography variant="body3_400" color={"primary.main"}>*/}
            {/*                                            (*/}
            {/*                                            <Link href="#" underline="hover">*/}
            {/*                                                1564654*/}
            {/*                                                <IconOpenInNew size={12} />*/}
            {/*                                            </Link>*/}
            {/*                                            )*/}
            {/*                                        </Typography>*/}
            {/*                                    </Grid>*/}

            {/*                                    /!* 여러개 *!/*/}
            {/*                                    <ImageFolderSmallOn width={16} height={16} />*/}
            {/*                                    <Grid item>*/}
            {/*                                        <Typography variant="body2_500">folder name</Typography>*/}
            {/*                                    </Grid>*/}
            {/*                                </Grid>*/}
            {/*                                <Divider sx={{ my: 2 }} />*/}
            {/*                                <Typography variant="body3_700" color="text.secondary">*/}
            {/*                                    복제 위치*/}
            {/*                                </Typography>*/}
            {/*                                <Stack mt={1} px={1} gap={1}>*/}
            {/*                                    <Grid container gap={1} alignItems={"center"} flexWrap={"nowrap"}>*/}
            {/*                                        <IconProjects*/}
            {/*                                            size={16}*/}
            {/*                                            htmlColor={theme.palette.greySecondary[500]}*/}
            {/*                                        />*/}
            {/*                                        <Typography variant="body3_400">자산 이름1</Typography>*/}
            {/*                                    </Grid>*/}
            {/*                                    <Grid container gap={1} alignItems={"center"} flexWrap={"nowrap"}>*/}
            {/*                                        <IconPath size={16} htmlColor={theme.palette.greySecondary[500]} />*/}
            {/*                                        <Typography variant="body3_400">folder name</Typography>*/}
            {/*                                    </Grid>*/}
            {/*                                    <Grid container gap={1} flexWrap={"nowrap"}>*/}
            {/*                                        <Grid item>*/}
            {/*                                            <IconCategory*/}
            {/*                                                size={16}*/}
            {/*                                                htmlColor={theme.palette.greySecondary[500]}*/}
            {/*                                            />*/}
            {/*                                        </Grid>*/}
            {/*                                        <Grid item>*/}
            {/*                                            <Grid container gap={0.5}>*/}
            {/*                                                <Chip label="카테고리1" size="small" variant="outlined" />*/}
            {/*                                                <Chip label="카테고리1" size="small" variant="outlined" />*/}
            {/*                                                <Chip label="카테고리1" size="small" variant="outlined" />*/}
            {/*                                                <Chip label="카테고리1" size="small" variant="outlined" />*/}
            {/*                                                <Chip label="카테고리1" size="small" variant="outlined" />*/}
            {/*                                            </Grid>*/}
            {/*                                        </Grid>*/}
            {/*                                    </Grid>*/}
            {/*                                </Stack>*/}
            {/*                            </Box>*/}
            {/*                            <Box>*/}
            {/*                                <Grid container alignItems={"center"} flexWrap={"nowrap"} gap={1}>*/}
            {/*                                    <IconHierarchyFolded*/}
            {/*                                        size={6}*/}
            {/*                                        htmlColor={theme.palette.greySecondary[500]}*/}
            {/*                                    />*/}
            {/*                                    <AssetTypeFlag width={20} height={16} type={"AUDIO"} />*/}
            {/*                                    <Grid item xs>*/}
            {/*                                        <Typography variant="body2_500">자산 이름1</Typography>*/}
            {/*                                        <Typography*/}
            {/*                                            variant="body3_400"*/}
            {/*                                            color={"primary.main"}*/}
            {/*                                            sx={{ ml: 1 }}*/}
            {/*                                        >*/}
            {/*                                            (*/}
            {/*                                            <Link href="#" underline="hover">*/}
            {/*                                                1564654*/}
            {/*                                                <IconOpenInNew size={12} />*/}
            {/*                                            </Link>*/}
            {/*                                            )*/}
            {/*                                        </Typography>*/}
            {/*                                    </Grid>*/}
            {/*                                    <Grid item>*/}
            {/*                                        <Tooltip title={"오류 메시지"}>*/}
            {/*                                            <IconButton circled size="small">*/}
            {/*                                                <IconErrorCircle size={16} color="error" />*/}
            {/*                                            </IconButton>*/}
            {/*                                        </Tooltip>*/}
            {/*                                    </Grid>*/}
            {/*                                </Grid>*/}
            {/*                                <Grid*/}
            {/*                                    container*/}
            {/*                                    gap={1}*/}
            {/*                                    mt={0.75}*/}
            {/*                                    px={2}*/}
            {/*                                    flexWrap={"nowrap"}*/}
            {/*                                    alignItems="center"*/}
            {/*                                >*/}
            {/*                                    <Typography variant="body3_400" color={"text.secondary"}>*/}
            {/*                                        원본 :*/}
            {/*                                    </Typography>*/}
            {/*                                    <Grid item>*/}
            {/*                                        <Typography variant="body3_400">원본 자산 이름</Typography>*/}
            {/*                                        <Typography*/}
            {/*                                            variant="body3_400"*/}
            {/*                                            color={"primary.main"}*/}
            {/*                                            sx={{ ml: 1 }}*/}
            {/*                                        >*/}
            {/*                                            (*/}
            {/*                                            <Link href="#" underline="hover">*/}
            {/*                                                1564654*/}
            {/*                                                <IconOpenInNew size={12} />*/}
            {/*                                            </Link>*/}
            {/*                                            )*/}
            {/*                                        </Typography>*/}
            {/*                                    </Grid>*/}
            {/*                                </Grid>*/}
            {/*                            </Box>*/}
            {/*                        </Stack>*/}
            {/*                        /!* //복제 *!/*/}

            {/*                        /!* 다운로드 *!/*/}
            {/*                        <Stack py={1.5} gap={2}>*/}
            {/*                            <Box>*/}
            {/*                                <Grid container flexWrap={"nowrap"}>*/}
            {/*                                    <Grid item>*/}
            {/*                                        <AssetTypeFlag width={20} height={16} type={"VIDEO"} />*/}
            {/*                                    </Grid>*/}
            {/*                                    <Grid item>*/}
            {/*                                        <Typography variant="body3_400">자산 이름1</Typography>*/}
            {/*                                        <Typography variant="body3_400">...외 </Typography>*/}
            {/*                                        <Typography variant="body3_700" color="primary.main">*/}
            {/*                                            99*/}
            {/*                                        </Typography>*/}
            {/*                                        <Typography variant="body3_400">개</Typography>*/}
            {/*                                    </Grid>*/}
            {/*                                </Grid>*/}
            {/*                                <Grid container flexWrap={"nowrap"} gap={1} mt={2}>*/}
            {/*                                    <Grid item>*/}
            {/*                                        <IconHierarchyFolded*/}
            {/*                                            size={6}*/}
            {/*                                            htmlColor={theme.palette.greySecondary[500]}*/}
            {/*                                        />*/}
            {/*                                    </Grid>*/}
            {/*                                    <Grid item>*/}
            {/*                                        <IconZip size={16} htmlColor={theme.palette.greySecondary[500]} />*/}
            {/*                                    </Grid>*/}
            {/*                                    <Grid item xs>*/}
            {/*                                        <Typography variant="body2_500">ZipFIleName</Typography>*/}
            {/*                                    </Grid>*/}
            {/*                                    <Grid item>*/}
            {/*                                        <Tooltip title={"오류 메시지"}>*/}
            {/*                                            <IconButton circled size="small">*/}
            {/*                                                <IconErrorCircle size={16} color="error" />*/}
            {/*                                            </IconButton>*/}
            {/*                                        </Tooltip>*/}
            {/*                                    </Grid>*/}
            {/*                                </Grid>*/}
            {/*                            </Box>*/}
            {/*                        </Stack>*/}
            {/*                        /!* //다운로드 *!/*/}

            {/*                        /!* 트랜스코드 *!/*/}
            {/*                        <Stack py={1.5} gap={2}>*/}
            {/*                            <Box>*/}
            {/*                                <Grid container flexWrap={"nowrap"}>*/}
            {/*                                    <Grid item>*/}
            {/*                                        <AssetTypeFlag width={20} height={16} type={"VIDEO"} />*/}
            {/*                                    </Grid>*/}
            {/*                                    <Grid item>*/}
            {/*                                        <Typography variant="body3_400">자산 이름1</Typography>*/}
            {/*                                        <Typography variant="body3_400">...외 </Typography>*/}
            {/*                                        <Typography variant="body3_700" color="primary.main">*/}
            {/*                                            99*/}
            {/*                                        </Typography>*/}
            {/*                                        <Typography variant="body3_400">개</Typography>*/}
            {/*                                    </Grid>*/}
            {/*                                </Grid>*/}
            {/*                                <Box mt={2}>*/}
            {/*                                    <Grid container flexWrap={"nowrap"} gap={1}>*/}
            {/*                                        <Grid item>*/}
            {/*                                            <IconHierarchyFolded*/}
            {/*                                                size={6}*/}
            {/*                                                htmlColor={theme.palette.greySecondary[500]}*/}
            {/*                                            />*/}
            {/*                                        </Grid>*/}
            {/*                                        <Grid item xs>*/}
            {/*                                            <Typography variant="body2_500">FileName.mp4</Typography>*/}
            {/*                                        </Grid>*/}
            {/*                                        <Grid item>*/}
            {/*                                            <Tooltip title={"오류 메시지"}>*/}
            {/*                                                <IconButton circled size="small">*/}
            {/*                                                    <IconErrorCircle size={16} color="error" />*/}
            {/*                                                </IconButton>*/}
            {/*                                            </Tooltip>*/}
            {/*                                        </Grid>*/}
            {/*                                    </Grid>*/}
            {/*                                    <Grid container flexWrap={"nowrap"} gap={1}>*/}
            {/*                                        <Grid item>*/}
            {/*                                            <IconHierarchyFolded*/}
            {/*                                                size={6}*/}
            {/*                                                htmlColor={theme.palette.greySecondary[500]}*/}
            {/*                                            />*/}
            {/*                                        </Grid>*/}
            {/*                                        <Grid item xs>*/}
            {/*                                            <Typography variant="body2_500">FileName.mp4</Typography>*/}
            {/*                                        </Grid>*/}
            {/*                                        <Grid item>*/}
            {/*                                            <Tooltip title={"오류 메시지"}>*/}
            {/*                                                <IconButton circled size="small">*/}
            {/*                                                    <IconErrorCircle size={16} color="error" />*/}
            {/*                                                </IconButton>*/}
            {/*                                            </Tooltip>*/}
            {/*                                        </Grid>*/}
            {/*                                    </Grid>*/}
            {/*                                </Box>*/}
            {/*                            </Box>*/}
            {/*                        </Stack>*/}
            {/*                        /!* //트랜스코드 *!/*/}

            {/*                        /!* 저장소 업로드 *!/*/}
            {/*                        <Stack py={1.5} gap={1}>*/}
            {/*                            <Grid container flexWrap={"nowrap"}>*/}
            {/*                                <AssetTypeFlag width={20} height={16} type={"VIDEO"} />*/}
            {/*                                <Grid item xs>*/}
            {/*                                    <Typography variant="body2_500">파일 이름1</Typography>*/}
            {/*                                </Grid>*/}
            {/*                                <Grid item>*/}
            {/*                                    <Tooltip title={"오류 메시지"}>*/}
            {/*                                        <IconButton circled size="small">*/}
            {/*                                            <IconErrorCircle size={16} color="error" />*/}
            {/*                                        </IconButton>*/}
            {/*                                    </Tooltip>*/}
            {/*                                </Grid>*/}
            {/*                            </Grid>*/}
            {/*                            <Grid container flexWrap={"nowrap"}>*/}
            {/*                                <AssetTypeFlag width={20} height={16} type={"VIDEO"} />*/}
            {/*                                <Grid item xs>*/}
            {/*                                    <Typography variant="body2_500">파일 이름1</Typography>*/}
            {/*                                </Grid>*/}
            {/*                                <Grid item>*/}
            {/*                                    <Tooltip title={"오류 메시지"}>*/}
            {/*                                        <IconButton circled size="small">*/}
            {/*                                            <IconErrorCircle size={16} color="error" />*/}
            {/*                                        </IconButton>*/}
            {/*                                    </Tooltip>*/}
            {/*                                </Grid>*/}
            {/*                            </Grid>*/}
            {/*                            <Grid container flexWrap={"nowrap"}>*/}
            {/*                                <AssetTypeFlag width={20} height={16} type={"VIDEO"} />*/}
            {/*                                <Grid item xs>*/}
            {/*                                    <Typography variant="body2_500">파일 이름1</Typography>*/}
            {/*                                </Grid>*/}
            {/*                                <Grid item>*/}
            {/*                                    <Tooltip title={"오류 메시지"}>*/}
            {/*                                        <IconButton circled size="small">*/}
            {/*                                            <IconErrorCircle size={16} color="error" />*/}
            {/*                                        </IconButton>*/}
            {/*                                    </Tooltip>*/}
            {/*                                </Grid>*/}
            {/*                            </Grid>*/}
            {/*                        </Stack>*/}
            {/*                        /!* //저장소 업로드 *!/*/}

            {/*                        /!* 저장소 작업 *!/*/}

            {/*                        /!* //저장소 작업 *!/*/}
            {/*                    </Grid>*/}
            {/*                </Grid>*/}
            {/*            </Box>*/}
            {/*        </Collapse>*/}
            {/*    </Box>*/}
            {/*    /!* //item *!/*/}
            {/*</Stack>*/}
            {/* //목록 영역 */}
        </Stack>
    );
};
